// Project shape lookup — drives the Smart Intake "Shape" step (UX v0.7).
//
// A "shape" is the cardinal kind of thing the user is building.
// Unlike the legacy `Domain` enum (which was 6 industry verticals), the
// shape grid answers "what is it?" which actually routes Phase 3 recipes.
//
// Each shape carries:
//   * a legacy `Domain` mapping so we can submit the existing intake
//     payload without a backend schema change,
//   * a friendly recipe/agent-roster preview for the UI,
//   * keyword heuristics so we can auto-detect the shape from the
//     problem statement.
//
// The shape value itself is kept client-side in sessionStorage so the
// Command Center can show a "Robotics / Edge" badge even though the
// backend only stores the legacy domain.

import type { Domain } from '@/types'

export type ShapeId =
  | 'web_saas'
  | 'mobile_app'
  | 'desktop_cli'
  | 'browser_ext'
  | 'game'
  | 'ml_ai'
  | 'robotics_edge'
  | 'iot_firmware'
  | 'data_pipeline'
  | 'api_integration'
  | 'dev_tool_lib'
  | 'security_secops'
  | 'scientific_stats'
  | 'blockchain_web3'
  | 'audio_music_gen'
  | 'image_video_gen'
  | 'marketing_site'
  | 'internal_tool'
  | 'research_proto'
  // Gate-G3.7 Phase 1 (plan/AI_ALM_Gate_G3_7_Implementation_Plan.md
  // §3) — six new shapes broaden the catalog so the depth-rubric
  // pack (PR-2) can route projects to discipline-specific
  // questions. Mirrors ``ProjectShape`` in
  // ``src/ai_alm/models/intake.py``; the parity test in
  // ``tests/unit/test_intake_shapes.py`` enforces the literal sets
  // stay identical.
  | 'realtime_collab'
  | 'search_rag'
  | 'fintech_payments'
  | 'devops_iac'
  | 'agentic_ai'
  | 'healthcare_clinical'
  | 'other'


export interface ShapeSpec {
  id: ShapeId
  label: string
  hint: string
  /** Lucide icon name. */
  icon: string
  /** Legacy backend Domain that best approximates this shape. */
  domain: Domain
  /** Friendly recipe slug used for display (backend routes by profile/domain). */
  recipe: string
  /** Which agents run during Phase 3 — purely for display. */
  agents: string[]
  /** Keywords to auto-detect from the problem statement. */
  keywords: string[]
}

export const PROJECT_SHAPES: ShapeSpec[] = [
  {
    id: 'web_saas',
    label: 'Web / SaaS',
    hint: 'B2B app, dashboards, portals',
    icon: 'globe',
    domain: 'enterprise_saas',
    recipe: 'web-saas-standard',
    // Phase-C (Agent Evaluation Implementation Plan §4): web/SaaS
    // projects almost always ship an HTTP surface, so the API
    // Contract Agent joins the standard roster here.
    // Phase-F (§7.2) added the Test Engineer for default acceptance-
    // test coverage; mirrored here so the Smart Intake roster preview
    // matches the backend recipe.
    agents: [
      'Business Analyst',
      'System Architect',
      'Diagrammer',
      'UI Sketcher',
      'API Contract Agent',
      'Test Engineer',
    ],
    keywords: ['saas', 'dashboard', 'b2b', 'multi-tenant', 'workspace', 'web app'],
  },
  {
    id: 'mobile_app',
    label: 'Mobile app',
    hint: 'iOS / Android native or RN',
    icon: 'smartphone',
    domain: 'consumer_product',
    recipe: 'mobile-app-standard',
    // Phase-G (§8.3): mobile apps gain the UX Researcher upstream
    // of the UI Sketcher so each wireframe can bind to a persona
    // goal. Mirrored from the backend recipe.
    // Wave C (2026-05-19): mobile is acceptance-test-heavy by
    // convention; the Test Engineer is added to mirror the
    // backend recipe.
    agents: [
      'Business Analyst',
      'System Architect',
      'UX Researcher',
      'UI Sketcher',
      'Diagrammer',
      'Test Engineer',
    ],
    keywords: ['ios', 'android', 'mobile app', 'react native', 'flutter', 'swift', 'kotlin'],
  },
  {
    id: 'desktop_cli',
    label: 'Desktop / CLI',
    hint: 'Native desktop tools, CLIs',
    icon: 'terminal',
    domain: 'enterprise_saas',
    recipe: 'desktop-cli-standard',
    agents: ['Business Analyst', 'System Architect', 'Diagrammer'],
    keywords: ['cli', 'command-line', 'desktop app', 'electron', 'tauri', 'native'],
  },
  {
    id: 'browser_ext',
    label: 'Browser extension',
    hint: 'Chrome / Firefox / Safari',
    icon: 'puzzle',
    domain: 'consumer_product',
    recipe: 'browser-ext-standard',
    agents: ['Business Analyst', 'System Architect', 'UI Sketcher'],
    keywords: ['browser extension', 'chrome extension', 'firefox addon', 'manifest v3'],
  },
  {
    id: 'game',
    label: 'Game',
    hint: 'Unity / Unreal / Godot / web',
    icon: 'gamepad-2',
    domain: 'consumer_product',
    recipe: 'game-prototype',
    agents: ['Business Analyst', 'System Architect', 'UI Sketcher', 'Diagrammer'],
    keywords: ['game', 'unity', 'unreal', 'godot', 'gameplay', 'mechanic', 'multiplayer'],
  },
  {
    id: 'ml_ai',
    label: 'ML / AI system',
    hint: 'Models, training, inference',
    icon: 'brain-circuit',
    domain: 'data_integration',
    recipe: 'ml-system-standard',
    // Phase-D split (Agent Evaluation Implementation Plan §5.2):
    // ML systems carry both regulatory analysis (Compliance — model
    // cards, GDPR) AND security analysis (Security Architect —
    // adversarial inputs, model theft). Mirrors the backend recipe.
    // Wave C (2026-05-19): ML data carries the heaviest data-
    // classification load in the catalog; Test Engineer added for
    // model-quality acceptance scenarios.
    agents: [
      'Business Analyst',
      'System Architect',
      'Compliance',
      'Security Architect',
      'Diagrammer',
      'Test Engineer',
    ],
    keywords: [
      'model',
      'training',
      'inference',
      'machine learning',
      'llm',
      'transformer',
      'embeddings',
      'rag',
      'classifier',
      'neural',
    ],
  },
  {
    id: 'robotics_edge',
    label: 'Robotics / Edge',
    hint: 'On-device, real-time, robotics',
    icon: 'bot',
    domain: 'data_integration',
    recipe: 'robotics-edge',
    // Phase-D split (§5.2): robotics has both regulatory exposure
    // (medical / industrial certification → Compliance) and
    // substantial security surface (firmware, OTA → Security
    // Architect). Mirrors the backend recipe.
    // Wave C (2026-05-19): safety-critical edge devices need
    // explicit acceptance tests; Test Engineer added.
    agents: [
      'Business Analyst',
      'System Architect',
      'Compliance',
      'Security Architect',
      'Diagrammer',
      'UI Sketcher',
      'Test Engineer',
    ],
    keywords: [
      'robot',
      'slam',
      'lidar',
      'odometry',
      'npu',
      'edge device',
      'real-time',
      'embedded ai',
      'autonomous',
      'navigation',
    ],
  },
  {
    id: 'iot_firmware',
    label: 'IoT / Firmware',
    hint: 'Embedded / microcontroller',
    icon: 'cpu',
    domain: 'data_integration',
    recipe: 'iot-firmware-standard',
    // Phase-D (§5.2): IoT/firmware is security-heavy with minimal
    // regulatory hooks — Security Architect only.
    // Wave C (2026-05-19): embedded products still have product
    // owners + a BRD; Business Analyst added to mirror the
    // backend recipe.
    agents: [
      'Business Analyst',
      'System Architect',
      'Security Architect',
      'Diagrammer',
    ],
    keywords: ['iot', 'firmware', 'microcontroller', 'mcu', 'embedded', 'sensor network'],
  },
  {
    id: 'data_pipeline',
    label: 'Data pipeline',
    hint: 'ETL / streaming / analytics',
    icon: 'git-branch',
    domain: 'data_integration',
    recipe: 'data-integration-standard',
    // Phase-F (§7.2): data pipelines gain the Test Engineer for
    // contract-level acceptance tests on the ETL surface.
    // Wave C (2026-05-19): pipelines move classified data and
    // need the full BRD + regulatory + security surface. BA,
    // Compliance, Security Architect added to mirror the backend
    // recipe (the robot-navigation retrospective root cause).
    agents: [
      'Business Analyst',
      'System Architect',
      'Compliance',
      'Security Architect',
      'Diagrammer',
      'API Contract Agent',
      'Test Engineer',
    ],
    keywords: ['etl', 'pipeline', 'ingestion', 'snowflake', 'bigquery', 'kafka', 'streaming'],
  },
  {
    id: 'api_integration',
    label: 'API / Integration',
    hint: 'Contracts, SDKs, webhooks',
    icon: 'plug',
    domain: 'data_integration',
    recipe: 'api-integration-standard',
    // Wave C (2026-05-19): APIs are the canonical Security Architect
    // surface; Business Analyst added so the project still carries
    // a BRD. Mirrors the backend recipe.
    agents: [
      'Business Analyst',
      'System Architect',
      'Security Architect',
      'Diagrammer',
      'API Contract Agent',
    ],
    keywords: ['api', 'openapi', 'webhook', 'integration', 'sdk', 'rest', 'graphql'],
  },
  {
    id: 'dev_tool_lib',
    label: 'Dev tool / Library',
    hint: 'SDKs, frameworks, CLIs you ship',
    icon: 'package',
    domain: 'enterprise_saas',
    recipe: 'dev-tool-library',
    // Wave C (2026-05-19): libraries / SDKs / CLIs are API surfaces;
    // the API Contract Agent and Test Engineer are added so the
    // public interface gets a canonical spec + contract tests.
    agents: [
      'Business Analyst',
      'System Architect',
      'Diagrammer',
      'API Contract Agent',
      'Test Engineer',
    ],
    // Distinct from `api_integration` (which *consumes* 3rd-party APIs):
    // a dev-tool ships a library/framework/CLI that other engineers adopt.
    keywords: [
      'library',
      'framework',
      'developer tool',
      'dev tool',
      'npm package',
      'crate',
      'package manager',
      'plugin system',
      'language server',
      'compiler',
      'linter',
    ],
  },
  {
    id: 'security_secops',
    label: 'Security / SecOps',
    hint: 'Pentest, SIEM, threat detection',
    icon: 'shield-check',
    domain: 'enterprise_saas',
    recipe: 'security-standard',
    // Phase-D split (§5.2) — heavy on Security Architect, light on
    // Compliance, both fire. Phase-F (§7.2) adds the Test Engineer
    // for security-acceptance scenarios. Mirrors the backend recipe.
    agents: [
      'Business Analyst',
      'System Architect',
      'Compliance',
      'Security Architect',
      'Diagrammer',
      'Test Engineer',
    ],
    keywords: [
      'security',
      'pentest',
      'penetration test',
      'vulnerability',
      'red team',
      'blue team',
      'threat detection',
      'siem',
      'soar',
      'iam',
      'zero trust',
      'owasp',
      'cve',
      'malware',
      'intrusion',
      'exploit',
    ],
  },
  {
    id: 'scientific_stats',
    label: 'Scientific / Stats',
    hint: 'Simulations, analysis, modeling',
    icon: 'sigma',
    domain: 'data_integration',
    recipe: 'scientific-computing',
    // Wave C (2026-05-19): reproducibility and numerical-correctness
    // invariants belong as `[pin]` / `[deferred]` rows under each
    // SR; Test Engineer added so the work carries explicit
    // acceptance scenarios.
    agents: [
      'Business Analyst',
      'System Architect',
      'Diagrammer',
      'Test Engineer',
    ],
    keywords: [
      'simulation',
      'monte carlo',
      'statistical',
      'statistics',
      'bayesian',
      'regression',
      'hypothesis test',
      'numerical',
      'scipy',
      'pandas',
      'r studio',
      'jupyter',
      'computational',
      'finite element',
      'differential equation',
      'hpc',
    ],
  },
  {
    id: 'blockchain_web3',
    label: 'Blockchain / Web3',
    hint: 'Smart contracts, on-chain, dApps',
    icon: 'link',
    domain: 'financial_services',
    recipe: 'blockchain-standard',
    // Phase-D split (§5.2): blockchain projects need both regulatory
    // analysis (KYC/AML, securities frameworks → Compliance) and
    // security analysis (smart-contract attack surface → Security
    // Architect). Mirrors the backend recipe.
    // Wave C (2026-05-19): smart contracts have explicit invariants
    // worth pinning; Test Engineer added for acceptance scenarios.
    agents: [
      'Business Analyst',
      'System Architect',
      'Compliance',
      'Security Architect',
      'Diagrammer',
      'Test Engineer',
    ],
    keywords: [
      'blockchain',
      'smart contract',
      'solidity',
      'ethereum',
      'evm',
      'web3',
      'dapp',
      'defi',
      'nft',
      'on-chain',
      'wallet',
      'rollup',
      'l2',
    ],
  },
  {
    id: 'audio_music_gen',
    label: 'Audio / Music gen',
    hint: 'Generative audio, DSP',
    icon: 'music',
    domain: 'consumer_product',
    recipe: 'generative-media-audio',
    // Phase-D split (§5.2): generative media is primarily about
    // regulatory exposure (copyright, content provenance) — Security
    // Architect is overkill. Compliance only. Mirrors the backend.
    agents: ['Business Analyst', 'System Architect', 'Compliance', 'UI Sketcher'],
    keywords: ['audio generation', 'music generation', 'dsp', 'synthesizer', 'tts', 'voice clone'],
  },
  {
    id: 'image_video_gen',
    label: 'Image / Video gen',
    hint: 'Generative visual media',
    icon: 'image',
    domain: 'consumer_product',
    recipe: 'generative-media-visual',
    agents: ['Business Analyst', 'System Architect', 'Compliance', 'UI Sketcher'],
    keywords: ['image generation', 'video generation', 'diffusion', 'stable diffusion', 'gan'],
  },
  {
    id: 'marketing_site',
    label: 'Marketing site',
    hint: 'Content / SEO / landing',
    icon: 'megaphone',
    domain: 'consumer_product',
    recipe: 'marketing-website',
    // Phase-G (§8.3): the marketing recipe is BA + UX Researcher +
    // UI Sketcher. The legacy "Content Strategist" preview was
    // aspirational (specced in brain/04-agents.md but deferred per
    // plan §14 open Q5) — drop it from the preview so the Smart
    // Intake roster matches the actual swarm.
    agents: ['Business Analyst', 'UX Researcher', 'UI Sketcher'],
    keywords: ['marketing site', 'landing page', 'seo', 'content site', 'microsite'],
  },
  {
    id: 'internal_tool',
    label: 'Internal tool',
    hint: 'Ops / workflow automation',
    icon: 'briefcase',
    domain: 'enterprise_saas',
    recipe: 'internal-tool-standard',
    // Phase-F (§7.2): internal tools gain the Test Engineer for
    // ops-dashboard acceptance tests.
    agents: ['Business Analyst', 'System Architect', 'UI Sketcher', 'Test Engineer'],
    keywords: ['internal tool', 'ops', 'admin panel', 'workflow', 'back office'],
  },
  {
    id: 'research_proto',
    label: 'Research proto',
    hint: 'Paper-grade proof of concept',
    icon: 'flask-conical',
    domain: 'data_integration',
    recipe: 'research-prototype',
    agents: ['Business Analyst', 'System Architect', 'Diagrammer'],
    keywords: ['research', 'prototype', 'paper', 'experiment', 'benchmark', 'proof of concept'],
  },
  // ---- Gate-G3.7 Phase 1 additions (PR-1) ----------------------------- //
  // Six new shapes per plan/AI_ALM_Gate_G3_7_Implementation_Plan.md §3.
  // Backend mirrors live in src/ai_alm/services/recipe_registry.py
  // (recipe entries) and src/ai_alm/models/intake.py (ProjectShape
  // Literal). Keep the agent-roster previews in lockstep with the
  // backend recipes — the parity test in tests/unit/test_intake_shapes.py
  // crosswalks the literal sets.
  {
    id: 'realtime_collab',
    label: 'Real-time / Collab',
    hint: 'Multiplayer, WebRTC, CRDT, presence',
    icon: 'users',
    domain: 'consumer_product',
    recipe: 'realtime-collab-standard',
    agents: [
      'Business Analyst',
      'System Architect',
      'UI Sketcher',
      'Diagrammer',
      'UX Researcher',
      'API Contract Agent',
      'Test Engineer',
      'SRD Technical Detailer',
      'Security Architect',
    ],
    keywords: [
      'multiplayer',
      'webrtc',
      'crdt',
      'presence',
      'real-time collab',
      'realtime collaboration',
      'live cursors',
      'co-editing',
      'collaborative editor',
      'state sync',
      'operational transform',
    ],
  },
  {
    id: 'search_rag',
    label: 'Search / RAG',
    hint: 'Vector index, retrieval, agentic RAG',
    icon: 'search',
    domain: 'data_integration',
    recipe: 'search-rag-standard',
    agents: [
      'Business Analyst',
      'System Architect',
      'Diagrammer',
      'API Contract Agent',
      'Test Engineer',
      'SRD Technical Detailer',
      'Compliance',
      'Security Architect',
    ],
    keywords: [
      'rag',
      'retrieval augmented',
      'retrieval-augmented',
      'vector index',
      'vector database',
      'vector store',
      'semantic search',
      'embedding search',
      'agentic rag',
      'reranker',
      'hybrid search',
      'pinecone',
      'weaviate',
      'milvus',
      'pgvector',
    ],
  },
  {
    id: 'fintech_payments',
    label: 'Fintech / Payments',
    hint: 'Card, ACH, PSD2, ledgers — strict idempotency',
    icon: 'credit-card',
    domain: 'financial_services',
    recipe: 'fintech-payments-standard',
    agents: [
      'Business Analyst',
      'System Architect',
      'Diagrammer',
      'UI Sketcher',
      'API Contract Agent',
      'Test Engineer',
      'SRD Technical Detailer',
      'Compliance',
      'Security Architect',
    ],
    keywords: [
      'payment',
      'payments',
      'card processing',
      'ach',
      'psd2',
      'sca',
      'strong customer authentication',
      'ledger',
      'double-entry',
      'idempotency',
      'merchant',
      'acquirer',
      'issuer',
      'stripe',
      'plaid',
      'open banking',
      'iso 20022',
    ],
  },
  {
    id: 'devops_iac',
    label: 'DevOps / IaC',
    hint: 'Platform, CI/CD, IaC, K8s operators',
    icon: 'server-cog',
    domain: 'enterprise_saas',
    recipe: 'devops-iac-standard',
    agents: [
      'Business Analyst',
      'System Architect',
      'Diagrammer',
      'API Contract Agent',
      'Test Engineer',
      'SRD Technical Detailer',
      'Security Architect',
    ],
    keywords: [
      'devops',
      'platform engineering',
      'iac',
      'infrastructure as code',
      'terraform',
      'pulumi',
      'kubernetes operator',
      'k8s operator',
      'helm chart',
      'ci/cd',
      'gitops',
      'argocd',
      'flux',
      'pipeline runner',
      'self-hosted runner',
      'sre',
    ],
  },
  {
    id: 'agentic_ai',
    label: 'Agentic AI',
    hint: 'Tool-using LLMs, multi-agent, MCP',
    icon: 'workflow',
    domain: 'data_integration',
    recipe: 'agentic-ai-standard',
    agents: [
      'Business Analyst',
      'System Architect',
      'Diagrammer',
      'API Contract Agent',
      'Test Engineer',
      'SRD Technical Detailer',
      'Compliance',
      'Security Architect',
    ],
    keywords: [
      'agentic',
      'agent system',
      'multi-agent',
      'tool use',
      'tool-using',
      'tool calling',
      'function calling',
      'mcp server',
      'model context protocol',
      'autonomous agent',
      'agent orchestration',
      'agent framework',
      'langgraph',
      'crewai',
      'autogen',
    ],
  },
  {
    id: 'healthcare_clinical',
    label: 'Healthcare (clinical)',
    hint: 'Clinical workflows, FHIR, EHR/EMR',
    icon: 'stethoscope',
    domain: 'healthcare_clinical',
    recipe: 'healthcare-clinical-full',
    agents: [
      'Business Analyst',
      'System Architect',
      'Compliance',
      'Security Architect',
      'Diagrammer',
      'UI Sketcher',
      'API Contract Agent',
      'Test Engineer',
      'SRD Technical Detailer',
    ],
    keywords: [
      'clinical workflow',
      'fhir',
      'ehr integration',
      'emr integration',
      'mro',
      'medication reconciliation',
      'patient chart',
      'clinical decision support',
      'cds',
      'hl7',
      'epic',
      'cerner',
      'allscripts',
      'meditech',
      '21 cfr part 11',
    ],
  },
  {
    id: 'other',
    label: 'Other',
    hint: 'Describe it and we route',
    icon: 'more-horizontal',
    domain: 'other',
    recipe: 'custom',
    agents: ['Business Analyst', 'System Architect'],
    keywords: [],
  },
]

export function getShape(id: ShapeId): ShapeSpec {
  return PROJECT_SHAPES.find((s) => s.id === id) ?? PROJECT_SHAPES[PROJECT_SHAPES.length - 1]
}

/**
 * Lightweight keyword-based shape detection from the problem statement.
 * Returns the best-matching shape plus a simple confidence (0..1).
 * Purely client-side, no LLM call.
 */
export function detectShape(problemStatement: string): {
  shape: ShapeSpec | null
  score: number
  matches: Record<ShapeId, number>
} {
  const normalized = problemStatement.toLowerCase()
  const matches = {} as Record<ShapeId, number>
  let best: ShapeSpec | null = null
  let bestHits = 0
  for (const s of PROJECT_SHAPES) {
    let hits = 0
    for (const kw of s.keywords) {
      if (normalized.includes(kw)) hits++
    }
    matches[s.id] = hits
    if (hits > bestHits) {
      bestHits = hits
      best = s
    }
  }
  const score = Math.min(1, bestHits / 3)
  return { shape: bestHits > 0 ? best : null, score, matches }
}

// ---- Audience -------------------------------------------------------------

export type AudienceTag =
  | 'engineers'
  | 'product'
  | 'execs'
  | 'auditors'
  | 'operators'
  | 'procurement'
  | 'designers'
  | 'end_users'
  | 'support'
  | 'academic'

export const AUDIENCE_OPTIONS: Array<{ id: AudienceTag; label: string; hint: string }> = [
  { id: 'engineers', label: 'Engineers', hint: 'Builders and reviewers' },
  { id: 'product', label: 'Product', hint: 'PMs, POs, scope owners' },
  { id: 'execs', label: 'Execs', hint: 'Sponsor summaries' },
  { id: 'auditors', label: 'Auditors', hint: 'Regulatory evidence' },
  { id: 'operators', label: 'Operators', hint: 'Day-2 ops & SREs' },
  { id: 'procurement', label: 'Procurement', hint: 'Vendor selection' },
  { id: 'designers', label: 'Designers', hint: 'UX / visual' },
  { id: 'end_users', label: 'End users', hint: 'Customer-facing UX' },
  { id: 'support', label: 'Support', hint: 'Help desks, CSMs' },
  { id: 'academic', label: 'Academic', hint: 'Research review' },
]

/**
 * Reduce a multi-select audience set to the single legacy `Audience` enum
 * the backend expects. Priority mirrors the tone most likely to be
 * non-negotiable — auditors > execs > engineers > procurement > mixed.
 */
export function collapseAudience(
  tags: AudienceTag[],
): import('@/types').Audience {
  if (tags.length === 0) return 'engineering'
  if (tags.length === 1) {
    switch (tags[0]) {
      case 'auditors': return 'auditor'
      case 'execs': return 'executive'
      case 'procurement': return 'procurement'
      case 'engineers':
      case 'operators':
      case 'support':
      case 'designers':
      case 'end_users':
      case 'product':
      case 'academic':
      default: return 'engineering'
    }
  }
  if (tags.includes('auditors')) return 'auditor'
  if (tags.includes('execs')) return 'executive'
  return 'mixed'
}

// ---- Regulatory keyword detection ----------------------------------------

export interface RegulatoryHit {
  flag: import('@/types').RegulatoryFlag
  label: string
  matched: string
}

/**
 * A cue entry can have multiple sub-groups that share a single backend flag
 * but represent meaningfully different regulations (e.g. FDA and HIPAA both
 * live under `fda_hipaa` but describe very different things). Splitting
 * them lets the displayed chip reflect *what actually matched* instead of
 * smearing unrelated regulations together — so a security tool that only
 * mentions "HIPAA" no longer gets labelled "FDA / HIPAA".
 */
interface RegulatoryCueGroup {
  /** Display label when this specific group matches. */
  label: string
  cues: string[]
}

export interface RegulatoryCueEntry {
  flag: import('@/types').RegulatoryFlag
  /** Label used when every group matches (or when there is only one group). */
  label: string
  groups: RegulatoryCueGroup[]
}

export const REGULATORY_CUES: RegulatoryCueEntry[] = [
  {
    flag: 'fda_hipaa',
    label: 'FDA / HIPAA',
    groups: [
      // HIPAA = US health data privacy (software handling PHI).
      {
        label: 'HIPAA',
        cues: ['hipaa', 'phi', 'ehr', 'emr', 'protected health information'],
      },
      // FDA = US medical device / drug regulation (21 CFR Part 11, SaMD).
      // Tightened from the old bare "clinical"/"patient" which matched
      // common non-medical English ("be patient with users", "clinical
      // success", etc.).
      {
        label: 'FDA',
        cues: [
          'fda',
          '21 cfr',
          'medical device software',
          'samd',
          'clinical trial',
          'patient safety',
          'patient data',
        ],
      },
    ],
  },
  {
    flag: 'gdpr',
    label: 'GDPR',
    groups: [{ label: 'GDPR', cues: ['gdpr', 'eu personal data', 'data subject'] }],
  },
  {
    flag: 'sox_pci_dss',
    label: 'SOX / PCI-DSS',
    groups: [
      {
        label: 'SOX / PCI-DSS',
        cues: ['sox', 'pci', 'credit card', 'financial controls'],
      },
    ],
  },
  {
    flag: 'fedramp_nist',
    label: 'FedRAMP / NIST',
    groups: [{ label: 'FedRAMP / NIST', cues: ['fedramp', 'nist 800', 'federal'] }],
  },
  {
    flag: 'iso_27001_soc2',
    label: 'ISO 27001 / SOC 2',
    groups: [{ label: 'ISO 27001 / SOC 2', cues: ['iso 27001', 'soc 2', 'soc2'] }],
  },
  {
    flag: 'section_508_wcag',
    label: 'Section 508 / WCAG',
    groups: [{ label: 'Section 508 / WCAG', cues: ['wcag', 'section 508', 'accessibility'] }],
  },
  // ---- Phase-I expansion (compliance-cue catalog 2025-Q1) ---------------- //
  // HITRUST CSF — security-control framework most often used to
  // demonstrate HIPAA compliance in healthcare. Treated as its own
  // cue (rather than folded into FDA/HIPAA) so the Compliance agent
  // can name it explicitly in the risk register.
  {
    flag: 'hitrust',
    label: 'HITRUST CSF',
    groups: [
      {
        label: 'HITRUST CSF',
        cues: ['hitrust', 'high trust', 'csf certification', 'hitrust csf'],
      },
    ],
  },
  // CCPA / CPRA — California consumer privacy. Sibling to GDPR.
  {
    flag: 'ccpa_cpra',
    label: 'CCPA / CPRA',
    groups: [
      {
        label: 'CCPA / CPRA',
        cues: [
          'ccpa',
          'cpra',
          'california consumer privacy',
          'california privacy rights',
        ],
      },
    ],
  },
  // FERPA / COPPA — student / minor data privacy.
  {
    flag: 'ferpa_coppa',
    label: 'FERPA / COPPA',
    groups: [
      // FERPA = student records (US Department of Education).
      {
        label: 'FERPA',
        cues: ['ferpa', 'student records', 'education records'],
      },
      // COPPA = children under 13 online privacy (US FTC).
      {
        label: 'COPPA',
        cues: ['coppa', "children's online privacy", 'under 13', 'minors data'],
      },
    ],
  },
  // GLBA — Gramm-Leach-Bliley Act, financial data privacy. Sibling
  // to SOX/PCI-DSS but covers consumer-financial-information privacy
  // rather than payment-card or audit controls.
  {
    flag: 'glba',
    label: 'GLBA',
    groups: [
      {
        label: 'GLBA',
        cues: [
          'glba',
          'gramm-leach-bliley',
          'gramm leach bliley',
          'financial privacy rule',
          'safeguards rule',
        ],
      },
    ],
  },
  // ITAR / EAR — US export controls. High security floor; defense /
  // dual-use technology. Word-boundary matching for the acronyms
  // (see SHORT_CUES) — "ear" is a common English word.
  {
    flag: 'itar_ear',
    label: 'ITAR / EAR',
    groups: [
      {
        label: 'ITAR',
        cues: ['itar', 'arms export', 'defense article'],
      },
      {
        label: 'EAR',
        cues: ['ear', 'export administration', 'dual-use', 'dual use export'],
      },
    ],
  },
  // CJIS — Criminal Justice Information Services. High-security
  // framework for law-enforcement systems. Pairs with FedRAMP/NIST
  // patterns but has its own audit cadence + advisory-policy doc.
  {
    flag: 'cjis',
    label: 'CJIS',
    groups: [
      {
        label: 'CJIS',
        cues: [
          'cjis',
          'criminal justice information',
          'law enforcement data',
          'lea data',
        ],
      },
    ],
  },
  // ---- Phase-J expansion (AI governance flags) ------------------------- //
  // ISO/IEC 42001:2023 — AI management system. The keyword set is
  // designed to fire on explicit standard names and on the common
  // synonyms ("AI management system") so a project that mentions
  // 42001 / AIMS at all gets the dedicated artifact.
  {
    flag: 'iso_42001',
    label: 'ISO/IEC 42001',
    groups: [
      {
        label: 'ISO/IEC 42001',
        cues: [
          'iso 42001',
          'iso/iec 42001',
          'iso-42001',
          '42001',
          'ai management system',
          'aims',
        ],
      },
    ],
  },
  // NIST AI RMF — AI risk management framework. Includes the GenAI
  // profile (AI 600-1) keywords so a project that names "GenAI" /
  // "generative AI" gets the AI RMF matrix automatically.
  {
    flag: 'nist_ai_rmf',
    label: 'NIST AI RMF',
    groups: [
      {
        label: 'NIST AI RMF',
        cues: [
          'nist ai rmf',
          'ai rmf',
          'nist ai 100',
          'nist 100-1',
          'ai risk management framework',
          'ai 600-1',
          'genai profile',
        ],
      },
    ],
  },
]

/**
 * Short acronym cues need word-boundary matching so they don't fire on
 * longer unrelated words. Without this, "phi" would match "phishing",
 * "philosophy", "philadelphia"; "fda" would match arbitrary substrings,
 * etc. Particularly important for security-domain problem statements.
 *
 * Phase-I additions (HITRUST, CCPA, CPRA, FERPA, COPPA, GLBA, ITAR,
 * EAR, CJIS) — every entry under ~5 letters that could collide with
 * everyday English is listed here. ``ear`` is the most aggressive
 * one (would match ``earlier``, ``hear``, etc.) so word-boundaries
 * are mandatory.
 */
const SHORT_CUES = new Set([
  'fda',
  'phi',
  'ehr',
  'emr',
  'sox',
  'pci',
  'soc2',
  'ccpa',
  'cpra',
  'ferpa',
  'coppa',
  'glba',
  'itar',
  'ear',
  'cjis',
  'lea data',
])

function cueMatches(normalizedHaystack: string, cue: string): boolean {
  if (SHORT_CUES.has(cue)) {
    const escaped = cue.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
    return new RegExp(`\\b${escaped}\\b`).test(normalizedHaystack)
  }
  return normalizedHaystack.includes(cue)
}

export function detectRegulatory(problemStatement: string): RegulatoryHit[] {
  const normalized = problemStatement.toLowerCase()
  const hits: RegulatoryHit[] = []
  for (const entry of REGULATORY_CUES) {
    const matchedGroups: Array<{ label: string; matched: string }> = []
    for (const group of entry.groups) {
      for (const cue of group.cues) {
        if (cueMatches(normalized, cue)) {
          matchedGroups.push({ label: group.label, matched: cue })
          break
        }
      }
    }
    if (matchedGroups.length === 0) continue
    // If every sub-group fired (e.g. HIPAA *and* FDA both appear), use the
    // combined entry label. Otherwise show only the sub-regulations that
    // actually matched, so a project mentioning HIPAA alone no longer
    // drags FDA along for the ride.
    const label =
      entry.groups.length > 1 && matchedGroups.length === entry.groups.length
        ? entry.label
        : matchedGroups.map((g) => g.label).join(' / ')
    hits.push({
      flag: entry.flag,
      label,
      matched: matchedGroups.map((g) => g.matched).join(', '),
    })
  }
  return hits
}

// ---- Frozen-noun detection ------------------------------------------------

/**
 * Heuristic: grab capitalized multi-word runs + common technical acronyms.
 * Just a preview — the Contextualist will produce the real frozen set.
 */
export function detectNouns(problemStatement: string): string[] {
  const nouns = new Set<string>()
  // Capitalised multi-word phrases (e.g. "Dosing Alert System").
  const phraseRegex = /\b([A-Z][A-Za-z0-9]+(?:\s+[A-Z][A-Za-z0-9]+)+)\b/g
  let match: RegExpExecArray | null
  while ((match = phraseRegex.exec(problemStatement)) !== null) {
    nouns.add(match[1].trim())
  }
  // Technical acronyms (SLAM, YOLO, NPU, LiDAR, GPU, etc.).
  const acronymRegex = /\b([A-Z]{2,6})\b/g
  while ((match = acronymRegex.exec(problemStatement)) !== null) {
    if (match[1] !== 'API') nouns.add(match[1])
  }
  // Known seed list, so something always shows up early in the textarea.
  const seeds = ['SLAM', 'YOLO', 'LiDAR', 'MobileNet', 'NPU', 'GPU', 'FPGA', 'IoT']
  for (const seed of seeds) {
    if (problemStatement.includes(seed)) nouns.add(seed)
  }
  return Array.from(nouns).slice(0, 10)
}
